import { createTheme, Theme } from '@mui/material/styles';
import {
  red,
  deepPurple,
  green,
  amber,
  orange,
  blueGrey,
} from '@mui/material/colors';
import { Property } from 'csstype';

declare module '@mui/styles/defaultTheme' {
  interface DefaultTheme extends Theme {}
}

export const typography = {
  fontWeightSemiBold: 600,
  fontFamily: [
    'Open Sans',
    '-apple-system',
    'BlinkMacSystemFont',
    'Segoe UI',
    'Roboto',
    'Helvetica Neue',
    'Ubuntu',
    'sans-serif',
  ].join(','),
  display1: {
    fontSize: '2.25rem',
    fontWeight: 400,
    lineHeight: 1.167,
  },
  display2: {
    fontSize: '1.125rem',
    fontWeight: 400,
    lineHeight: 1.2,
  },
  h1: {
    fontSize: '1.75rem',
    fontWeight: 600,
  },
  h2: {
    fontSize: '1.375rem',
    fontWeight: 600,
  },
  h3: {
    fontSize: '1rem',
    fontWeight: 600,
  },
  h4: {
    fontSize: '0.875rem',
    fontWeight: 600,
  },
  h5: {
    fontSize: '0.75rem',
    fontWeight: 600,
  },
  body1: {
    fontSize: '0.875rem',
    fontWeight: 400,
  },
  body2: {
    fontSize: '0.75rem',
    fontWeight: 400,
  },
  subtitle: {
    fontSize: '0.75rem',
    fontWeight: 600,
    textTransform: 'uppercase' as Property.TextTransform,
    lineHeight: 1.334,
  },
  caption: {
    fontSize: '0.75rem',
    fontWeight: 400,
    lineHeight: 1.66,
  },
};

export const shape = {
  borderRadius: 4,
};

export const h1 = {
  neutral: {
    gray: {
      5: '#F7F8F9',
      10: '#E7E8EC',
      20: '#D6D7DD',
      30: '#C5C7CD',
      40: '#AAACB4',
      50: '#92959E',
      60: '#727581',
      70: '#5C5A6A',
      80: '#3A3C45',
      90: '#0A0A0A',
    },
  },
  primary: {
    blue: '#1832FE',
    green: '#3FFD5A',
    pink: '#F922A3',
  },
  secondary: {
    aqua: '#53C1D9',
    brown: '#571D21',
    burgundy: '#7E0462',
    champagne: '#E4DAA2',
    copper: '#AA712C',
    darkPink: '#E10E71',
    forestGreen: '#0C9B66',
    gold: '#F1A92E',
    midBlue: '#285DF2',
    mint: '#84D5B9',
    navy: '#12224F',
    ocean: '#1E43A9',
    orange: '#FF641F',
    peach: '#FFB185',
    purple: '#905FF0',
    violet: '#D653D9',
  },
};

export const MuiTheme = createTheme({
  palette: {
    primary: {
      dim: blueGrey[50],
      main: '#27557A',
    },
    secondary: {
      dim: '#e9f4fb',
      main: '#4AA4E0',
      contrastText: '#FFFFFF',
    },
    background: {
      paper: '#fefefe',
      default: '#fafafa',
    },
    addition: {
      main: 'rgba(151, 234, 151, 0.25)',
      light: '#acf1bd',
    },
    deletion: {
      main: 'rgba(251, 175, 175, 0.25)',
      light: '#fdb7c0',
    },
    error: {
      dim: red[50],
      main: red[500],
    },
    warning: {
      dim: orange[50],
      main: orange[500],
    },
    highlight: {
      dim: amber[50],
      main: amber[500],
      dark: '#cba033',
    },
    info: {
      dim: '#e9f4fb',
      main: '#4AA4E0',
    },
    success: {
      dim: green[50],
      main: green[500],
    },
    merged: {
      dim: deepPurple[50],
      main: deepPurple[500],
    },
    h1,
  },
  typography,
  shape,
  zIndex: {
    overlay: 1400,
    bar: 1000,
  },
});

export const MuiDarkTheme = createTheme({
  palette: {
    mode: 'dark',
    text: {
      primary: '#bac2cc',
      secondary: '#8d96a1',
    },
    background: {
      paper: '#353b44',
      default: '#22272e',
    },
    primary: {
      dim: '#0099ff33',
      main: '#00a3ff',
    },
    secondary: {
      dim: '#0099ff33',
      main: '#00a3ff',
      contrastText: '#FFFFFF',
    },
    addition: {
      main: 'rgba(70, 149, 74, 0.15)',
      light: 'rgba(70, 149, 74, 0.4)',
    },
    deletion: {
      main: 'rgba(229, 83, 75, 0.15)',
      light: 'rgba(229, 83, 75, 0.4)',
    },
    error: {
      dim: '#5c0d0f',
      main: '#e8494d',
    },
    warning: {
      dim: '#643d03',
      main: '#d2820f',
    },
    info: {
      dim: '#0099ff33',
      main: '#00a3ff',
    },
    success: {
      dim: '#0e4e1a',
      main: '#39b354',
    },
    highlight: {
      dim: '#584820',
      main: '#cba033',
      dark: amber[700],
    },
    merged: {
      dim: '#26124a',
      main: '#b082ff',
    },
    h1,
  },
  typography,
  shape,
  zIndex: {
    overlay: 1400,
    bar: 1000,
  },
});
